import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  createMeeting,
  deleteMeeting,
  getMeeting,
  getProjectShareableUsers,
  listMeetings,
  updateMeeting,
} from '../api/meetings-api';
import type {
  MeetingDraft,
  MeetingFilters,
  MeetingPatch,
} from '../types';

export const meetingKeys = {
  all: ['meetings'] as const,
  lists: (projectId: number) =>
    [...meetingKeys.all, 'list', projectId] as const,
  list: (projectId: number, filters: MeetingFilters) =>
    [...meetingKeys.lists(projectId), filters] as const,
  detail: (id: string) => [...meetingKeys.all, 'detail', id] as const,
  projectUsers: (projectId: number) =>
    [...meetingKeys.all, 'project-users', projectId] as const,
};

export const useMeetings = (
  projectId: number | undefined,
  filters: MeetingFilters = {},
) =>
  useQuery({
    queryKey: meetingKeys.list(projectId ?? 0, filters),
    queryFn: () => listMeetings(projectId!, filters),
    enabled: !!projectId,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

export const useMeeting = (id: string | undefined) =>
  useQuery({
    queryKey: meetingKeys.detail(id ?? ''),
    queryFn: () => getMeeting(id!),
    enabled: !!id,
    staleTime: 60_000,
  });

export const useMeetingProjectUsers = (projectId: number | undefined) =>
  useQuery({
    queryKey: meetingKeys.projectUsers(projectId ?? 0),
    queryFn: () => getProjectShareableUsers(projectId!),
    enabled: !!projectId,
    staleTime: 5 * 60_000,
  });

export const useMeetingMutations = (projectId: number | undefined) => {
  const queryClient = useQueryClient();

  const invalidateLists = () => {
    if (!projectId) return;
    queryClient.invalidateQueries({
      queryKey: meetingKeys.lists(projectId),
    });
  };

  const createItem = useMutation({
    mutationFn: (draft: MeetingDraft) => createMeeting(draft),
    onSuccess: () => invalidateLists(),
  });

  const updateItem = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: MeetingPatch }) =>
      updateMeeting(id, patch),
    onSuccess: (result) => {
      queryClient.setQueryData(meetingKeys.detail(result.id), result);
      invalidateLists();
    },
  });

  const deleteItem = useMutation({
    mutationFn: (id: string) => deleteMeeting(id),
    onSuccess: () => invalidateLists(),
  });

  return { createItem, updateItem, deleteItem };
};
